import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * SDS Photo Display — mismo patrón que el export PDF de Muestras
 * (A3 apaisado: cabecera Stage + 2 fotos front/back + pie).
 * Proporciones derivadas de muestrasPdfExport.js (PAGE 1190.55×841.89).
 */
export const TEMPLATE_WIDTH = 3508;
export const TEMPLATE_HEIGHT = 2480;

const MARGIN_X = 0.04;
const PHOTO_GAP = 0.0235;
const SLOT_W = (1 - MARGIN_X * 2 - PHOTO_GAP) / 2;
const PHOTOS_Y = 0.214;
const PHOTOS_H = 0.648;
const HEADER_LINE_Y = 0.19;

export const config = {
  width: TEMPLATE_WIDTH,
  height: TEMPLATE_HEIGHT,
  regions: [
    {
      id: "image1",
      x: MARGIN_X,
      y: PHOTOS_Y,
      width: SLOT_W,
      height: PHOTOS_H,
    },
    {
      id: "image2",
      x: MARGIN_X + SLOT_W + PHOTO_GAP,
      y: PHOTOS_Y,
      width: SLOT_W,
      height: PHOTOS_H,
    },
  ],
  lines: [
    {
      x1: MARGIN_X,
      x2: 1 - MARGIN_X,
      y: HEADER_LINE_Y,
      color: "#464646",
      strokeWidth: 3,
    },
  ],
  textFields: [
    {
      id: "stage_1",
      staticText: "STAGE",
      x: MARGIN_X,
      y: 0.028,
      width: 0.22,
      height: 0.04,
      fontSize: 72,
      fontWeight: "normal",
      color: "#9b9b9b",
    },
    {
      id: "stage_2",
      staticText: "DESIGN",
      x: MARGIN_X,
      y: 0.068,
      width: 0.22,
      height: 0.04,
      fontSize: 72,
      fontWeight: "normal",
      color: "#9b9b9b",
    },
    {
      id: "stage_3",
      staticText: "STUDIO",
      x: MARGIN_X,
      y: 0.108,
      width: 0.22,
      height: 0.04,
      fontSize: 72,
      fontWeight: "normal",
      color: "#9b9b9b",
    },
    {
      id: "marca",
      x: 0.42,
      y: 0.04,
      width: 0.54,
      height: 0.12,
      fontSize: 110,
      fontWeight: "normal",
      color: "#afafaf",
      textAlign: "right",
    },
    {
      id: "footer_title",
      staticText: "Stage Design Studio",
      x: MARGIN_X,
      y: 0.895,
      width: 1 - MARGIN_X * 2,
      height: 0.035,
      fontSize: 36,
      fontWeight: "bold",
      color: "#282828",
      textAlign: "center",
    },
    {
      id: "footer_contact",
      staticText: "Irène Guijt | irene@stagedesign.es | +34 667 765 007",
      x: MARGIN_X,
      y: 0.935,
      width: 1 - MARGIN_X * 2,
      height: 0.03,
      fontSize: 26,
      fontWeight: "normal",
      color: "#646464",
      textAlign: "center",
    },
  ],
};

/** Preview SDS Photo Display (selector + previsualizar sin IA) */
function PlantillaSimplePreview({ marca = "MARCA", className }) {
  const slotLeft2 = (MARGIN_X + SLOT_W + PHOTO_GAP) * 100;

  return (
    <Box
      className={className}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 72,
        aspectRatio: `${TEMPLATE_WIDTH}/${TEMPLATE_HEIGHT}`,
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 0.5,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Stage wordmark */}
      <Box
        sx={{
          position: "absolute",
          left: `${MARGIN_X * 100}%`,
          top: "2.5%",
          lineHeight: 1.15,
        }}
      >
        {["STAGE", "DESIGN", "STUDIO"].map((line) => (
          <Typography
            key={line}
            sx={{
              fontSize: ".18rem",
              color: "#9b9b9b",
              letterSpacing: 0.4,
              fontWeight: 400,
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>

      {/* Título / marca (derecha) */}
      <Typography
        sx={{
          position: "absolute",
          right: `${MARGIN_X * 100}%`,
          top: "4%",
          maxWidth: "52%",
          fontSize: ".28rem",
          color: "#afafaf",
          fontWeight: 400,
          textAlign: "right",
          lineHeight: 1.05,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {(marca || "MARCA").toString().toUpperCase()}
      </Typography>

      {/* Raya cabecera */}
      <Box
        sx={{
          position: "absolute",
          left: `${MARGIN_X * 100}%`,
          right: `${MARGIN_X * 100}%`,
          top: `${HEADER_LINE_Y * 100}%`,
          height: "1px",
          bgcolor: "#464646",
        }}
      />

      {/* Foto front */}
      <Box
        sx={{
          position: "absolute",
          left: `${MARGIN_X * 100}%`,
          top: `${PHOTOS_Y * 100}%`,
          width: `${SLOT_W * 100}%`,
          height: `${PHOTOS_H * 100}%`,
          border: "1px solid #dcdcdc",
          bgcolor: "#fcfcfc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" sx={{ fontSize: ".2rem", color: "#aaa" }}>
          1 · FRONT
        </Typography>
      </Box>

      {/* Foto back */}
      <Box
        sx={{
          position: "absolute",
          left: `${slotLeft2}%`,
          top: `${PHOTOS_Y * 100}%`,
          width: `${SLOT_W * 100}%`,
          height: `${PHOTOS_H * 100}%`,
          border: "1px solid #dcdcdc",
          bgcolor: "#fcfcfc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" sx={{ fontSize: ".2rem", color: "#aaa" }}>
          2 · BACK
        </Typography>
      </Box>

      {/* Pie */}
      <Typography
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "89.5%",
          textAlign: "center",
          fontSize: ".16rem",
          fontWeight: 700,
          color: "#282828",
        }}
      >
        Stage Design Studio
      </Typography>
      <Typography
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "93.5%",
          textAlign: "center",
          fontSize: ".12rem",
          color: "#646464",
        }}
      >
        Irène Guijt | irene@stagedesign.es | +34 667 765 007
      </Typography>
    </Box>
  );
}

export default PlantillaSimplePreview;
